import { ExternalLink } from "@/components/ExternalLink";
import { Tables } from "@/gen/tableNames/tables";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { Clients } from "@/lib/clients";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type A5Transaction = {
  id: number;
  date: Date;
  a5_portfolio_client?: string;
  a5_portfolio_id?: number;
  a5_portfolio_name?: string;
  a5_static_datum_id?: number;
  a5_static_datum_name?: string;
  a5_portfolio_reporting_currency?: string;
  quantity?: number;
  market_value_ptf_ccy?: number;
  ptf_currency?: string;
  market_value_local_ccy?: number;
  local_currency?: string;
  category?: string;
  transfer?: string;
  description?: string;
  fees?: number;
  price_local_ccy?: number;
  tax?: number;
  custodian_broker_fee?: number;
  broker_fee?: number;
  other_fee?: number;
  fee_currency?: string;
  fee_type?: string;
};

const t = (key: string, defaultValue: string) =>
  i18n.t(`${Tables.A5_TRANSACTIONS}.columns.${key}`, {
    ns: "tables",
    defaultValue,
  });

export const porfolios_columns: ColumnDef<A5Transaction>[] = [
  {
    accessorKey: "id",
    header: t("id", "ID"),
  },
  {
    accessorKey: "date",
    header: t("date", "Date"),
    cell: ({ row }) => {
      return (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue("date")))}
        </div>
      );
    },
  },
  {
    accessorKey: "a5_portfolio_client",
    header: t("a5_portfolio_client", "Client"),
  },
  {
    accessorKey: "a5_portfolio_name",
    header: t("a5_portfolio_name", "Account"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${Clients.A5}/${Tables.A5_PORTFOLIOS}/${row.original.a5_portfolio_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue("a5_portfolio_name")}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "a5_static_datum_name",
    header: t("a5_static_datum_name", "Asset"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${Clients.A5}/${Tables.A5_STATIC_DATA}/${row.original.a5_static_datum_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue("a5_static_datum_name")}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "quantity",
    header: () => <div className="text-right">{t("quantity", "Quantity")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("quantity"))}
        </div>
      );
    },
  },
  {
    accessorKey: "market_value_ptf_ccy",
    header: () => (
      <div className="text-right">
        {t("market_value_ptf_ccy", "Market value (reporting ccy)")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("market_value_ptf_ccy"))}
        </div>
      );
    },
  },
  {
    header: t(
      "a5_portfolio_reporting_currency",
      "Portfolio reporting currency",
    ),
    accessorKey: "a5_portfolio_reporting_currency",
  },
  {
    accessorKey: "market_value_local_ccy",
    header: () => (
      <div className="text-right">
        {t("market_value_local_ccy", "Market value local ccy")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("market_value_local_ccy"))}
        </div>
      );
    },
  },
  {
    accessorKey: "local_currency",
    header: t("local_currency", "Local currency"),
  },
  {
    accessorKey: "fees",
    header: () => <div className="text-right">{t("fees", "Fees")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">{formatNumber(row.getValue("fees"))}</div>
      );
    },
  },
  {
    accessorKey: "fee_currency",
    header: t("fee_currency", "Fee currency"),
  },
  {
    accessorKey: "description",
    header: t("description", "Description"),
  },
  {
    accessorKey: "category",
    header: t("category", "Movement type"),
  },
  {
    accessorKey: "transfer",
    header: t("transfer", "Transfer type"),
    cell: ({ row }) => {
      return <div className="text-left">{row.getValue("transfer")}</div>;
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];

// export const porfolios_audit_columns = getAuditColumns<A5Transaction>(porfolios_columns)
